import logger from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/layout/Navbar';
import { MobileNav } from '../components/layout/MobileNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatCAD } from '../utils/currency';
import { 
  Send, 
  Loader2, 
  CheckCircle,
  ArrowLeft,
  Mail,
  DollarSign,
  MessageSquare
} from 'lucide-react';

export default function TransferPage() {
  const { t } = useLanguage();
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [balance, setBalance] = useState(0);
  const [formData, setFormData] = useState({
    recipient_email: '',
    amount: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const [transferResult, setTransferResult] = useState(null);

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const response = await api.get('/wallet/balance');
        setBalance(response.data.balance);
      } catch (error) {
        logger.error('Error fetching balance:', error);
      }
    };
    fetchBalance();
  }, [api]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (amount > balance) {
      toast.error(t('transfer.insufficient'));
      return;
    }
    
    if (!formData.recipient_email) {
      toast.error('Please enter recipient email');
      return;
    }
    
    setConfirmOpen(true);
  };

  const confirmTransfer = async () => {
    setLoading(true);
    setConfirmOpen(false);
    
    try {
      const response = await api.post('/wallet/transfer', {
        recipient_email: formData.recipient_email,
        amount: parseFloat(formData.amount),
        description: formData.description || 'P2P Transfer'
      });
      
      setTransferResult(response.data);
      setSuccess(true);
      toast.success(t('transfer.success'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-0">
        <Navbar />
        <main className="max-w-lg mx-auto px-4 py-12">
          <Card className="text-center animate-fade-in-up">
            <CardContent className="pt-12 pb-8">
              <div className="h-20 w-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">{t('transfer.success')}</h2>
              <p className="text-muted-foreground mb-2">
                {formatCAD(parseFloat(formData.amount))} {t('transfer.sent_to') || 'sent to'}
              </p>
              <p className="font-medium mb-4">{formData.recipient_email}</p>
              {transferResult?.reference && (
                <p className="text-sm text-muted-foreground mb-8">
                  {t('transactions.reference')}: {transferResult.reference}
                </p>
              )}
              <Button onClick={() => navigate('/dashboard')} className="rounded-full px-8" data-testid="back-to-dashboard">
                {t('topup.back_to_dashboard')}
              </Button>
            </CardContent>
          </Card>
        </main>
        <MobileNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navbar />
      
      <main className="max-w-lg mx-auto px-4 py-6 sm:py-8">
        <Button 
          variant="ghost" 
          className="mb-4" 
          onClick={() => navigate('/dashboard')}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('dashboard.welcome').split(',')[0]}
        </Button>

        <Card className="animate-fade-in-up">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="h-12 w-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Send className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <CardTitle>{t('transfer.title')}</CardTitle>
                <CardDescription>{t('transfer.subtitle')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Available Balance */}
              <div className="bg-muted/50 rounded-xl p-4 flex justify-between items-center">
                <span className="text-sm text-muted-foreground">{t('transfer.available')}</span>
                <span className="font-bold text-lg">{formatCAD(balance)}</span>
              </div>

              {/* Recipient Email */}
              <div className="space-y-2">
                <Label htmlFor="recipient">{t('transfer.recipient')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="recipient"
                    type="email"
                    placeholder={t('transfer.recipient_placeholder')}
                    className="pl-10 h-12 rounded-xl"
                    value={formData.recipient_email}
                    onChange={(e) => setFormData({ ...formData, recipient_email: e.target.value })}
                    required
                    data-testid="transfer-recipient"
                  />
                </div>
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <Label htmlFor="amount">{t('transfer.amount')}</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="amount"
                    type="number"
                    placeholder={t('transfer.amount_placeholder')}
                    className="pl-10 h-12 rounded-xl"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    min="0.01"
                    step="0.01"
                    max={balance}
                    required
                    data-testid="transfer-amount"
                  />
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">{t('transfer.description')}</Label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="description"
                    type="text"
                    placeholder={t('transfer.description_placeholder')}
                    className="pl-10 h-12 rounded-xl"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="transfer-description"
                  />
                </div>
              </div>

              <Button 
                type="submit"
                className="w-full h-12 rounded-xl" 
                disabled={loading || !formData.amount || parseFloat(formData.amount) > balance}
                data-testid="transfer-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    {t('transfer.send')}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('transfer.confirm_title')}</DialogTitle>
            <DialogDescription>
              {t('transfer.confirm_message')} <strong>{formatCAD(parseFloat(formData.amount || 0))}</strong> {t('transfer.to')} <strong>{formData.recipient_email}</strong>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="transfer-cancel">
              {t('transfer.cancel')}
            </Button>
            <Button onClick={confirmTransfer} disabled={loading} data-testid="transfer-confirm">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('transfer.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MobileNav />
    </div>
  );
}
